import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, FilePlus2, FileX2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VisorArchivo from "@/components/VisorArchivo";
import { Avatar, Badge, Card, Modal, PageHeader, TIPO_JUST, btnPrim, btnSec, inputCls } from "@/components/rrhh/ui";

const ESTADOS = [
  { id: "pendiente", label: "Pendientes" },
  { id: "aprobado", label: "Aprobados" },
  { id: "rechazado", label: "Rechazados" },
];
const ESTADO_COLOR = { pendiente: "amarillo", aprobado: "verde", rechazado: "rojo" };
const POR_PAGINA = 8;

export default function JustificativosPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const esLiq = user?.rol === "liquidacion";
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("pendiente");
  const [personaId, setPersonaId] = useState("");
  const [pagina, setPagina] = useState(1);
  const [visor, setVisor] = useState(null); // { url, empleado_id }
  const [resolver, setResolver] = useState(null); // justificativo
  const [comentario, setComentario] = useState("");
  const [descuenta, setDescuenta] = useState(false);
  const [cargar, setCargar] = useState(null); // { empleado_id }
  const [form, setForm] = useState({ tipo: "turno_medico", fecha: "", descripcion: "" });
  const [archivo, setArchivo] = useState(null);
  const fileRef = useRef(null);

  const { data: empleados } = useQuery({ queryKey: ["empleados"], queryFn: async () => (await api.get("/empleados")).data });
  const { data: justs, isLoading } = useQuery({
    queryKey: ["justificativos", tab],
    queryFn: async () => (await api.get("/rrhh/justificativos", { params: { estado: tab } })).data,
    refetchInterval: 5000,
  });

  useEffect(() => {
    const c = params.get("cargar");
    if (c) {
      setCargar({ empleado_id: c });
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => { setPagina(1); }, [tab, personaId]);

  const filtrados = useMemo(
    () => (justs || []).filter((j) => !personaId || j.empleado_id === personaId),
    [justs, personaId],
  );
  const paginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const enPagina = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["justificativos"] });
    qc.invalidateQueries({ queryKey: ["alertas"] });
    qc.invalidateQueries({ queryKey: ["alertas-resumen"] });
    qc.invalidateQueries({ queryKey: ["hoy"] });
  };

  const mutResolver = useMutation({
    mutationFn: async ({ id, accion }) => api.post(`/rrhh/justificativos/${id}/resolver`, { accion, comentario, descuenta }),
    onSuccess: (_r, v) => {
      invalidar();
      setResolver(null);
      toast.success(v.accion === "aprobado" ? "Justificativo aprobado" : "Justificativo rechazado");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const mutCargar = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("empleado_id", cargar.empleado_id);
      fd.append("tipo", form.tipo);
      fd.append("fecha", form.fecha);
      fd.append("descripcion", form.descripcion);
      fd.append("archivo", archivo);
      return api.post("/rrhh/justificativos", fd);
    },
    onSuccess: () => {
      invalidar();
      setCargar(null);
      setArchivo(null);
      setForm({ tipo: "turno_medico", fecha: "", descripcion: "" });
      toast.success("Justificativo cargado — queda pendiente de aprobación");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const abrirResolver = (j) => { setResolver(j); setComentario(""); setDescuenta(false); };

  const verComprobante = async (j) => {
    setVisor({ url: j.archivo_url });
    try {
      await api.post("/rrhh/registrar-vista", { que: "comprobante", referencia: j.archivo_url, empleado_id: j.empleado_id });
    } catch { /* el registro de vista no bloquea */ }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-justificativos">
      <PageHeader
        titulo="Justificativos"
        subtitulo="Los comprobantes que mandan desde el kiosco y los que cargás vos. Al aprobar uno, el día queda con su novedad."
        testid="header-justificativos"
      >
        <select value={tab} onChange={(e) => setTab(e.target.value)} className={inputCls} data-testid="filtro-estado-just">
          {ESTADOS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={personaId} onChange={(e) => setPersonaId(e.target.value)} className={inputCls} data-testid="filtro-persona-just">
          <option value="">Todas las personas</option>
          {empleados?.map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre}</option>)}
        </select>
        {!esLiq && (
          <button className={`${btnPrim} flex items-center gap-2`} onClick={() => setCargar({ empleado_id: "" })} data-testid="btn-cargar-justificativo">
            <FilePlus2 size={15} /> Cargar uno
          </button>
        )}
      </PageHeader>

      <Card
        testid="card-comprobantes"
        titulo="Comprobantes"
        subtitulo="Son datos sensibles: cada vez que se abre uno queda registrado quién lo vio."
        bodyCls="p-0"
      >
        {isLoading && <p className="px-5 py-10 text-center text-sm text-gray-500">Cargando…</p>}
        {!isLoading && enPagina.length === 0 && (
          <div className="flex flex-col items-center px-5 py-14 text-center" data-testid="just-vacio">
            <FileX2 size={28} className="text-gray-600" />
            <p className="mt-3 text-sm font-semibold text-gray-300">
              No hay justificativos {ESTADOS.find((t) => t.id === tab)?.label.toLowerCase()}
            </p>
            <p className="text-xs text-gray-500">Cuando alguien mande uno desde el kiosco o el celular, aparece acá.</p>
          </div>
        )}
        <div className="divide-y divide-white/5">
          {enPagina.map((j) => (
            <div key={j.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`just-${j.id.slice(0, 8)}`}>
              <div className="flex min-w-48 items-center gap-3">
                <Avatar iniciales={`${j.empleado_nombre.split(",")[1]?.trim()[0] || ""}${j.empleado_nombre.split(",")[0][0]}`} size="sm" />
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-white">
                    {j.empleado_nombre} <span className="font-mono-num text-xs text-gray-500">({j.legajo})</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    <span className="font-mono-num">{fmtFecha(j.fecha)}</span> · {j.cargado_por === "rrhh" ? "Cargado por RRHH" : "Desde el kiosco"}
                  </p>
                </div>
              </div>
              <Badge color="azul">{TIPO_JUST[j.tipo] || j.tipo}</Badge>
              <div className="min-w-40 flex-1">
                <p className="text-sm text-gray-300">{j.descripcion || <span className="text-gray-600">Sin descripción</span>}</p>
                {j.comentario_rrhh && <p className="mt-0.5 text-xs italic text-gray-500">“{j.comentario_rrhh}”</p>}
              </div>
              {j.estado !== "pendiente" && (
                <span className="flex items-center gap-2">
                  <Badge color={ESTADO_COLOR[j.estado]}>{j.estado}</Badge>
                  {j.estado === "aprobado" && (j.descuenta
                    ? <Badge color="rojo">Descuenta adicional</Badge>
                    : <Badge color="verde">No descuenta</Badge>)}
                </span>
              )}
              <span className="ml-auto flex gap-2">
                {j.archivo_url && (
                  <button className={`${btnSec} !px-2.5 !py-1.5 text-xs`} onClick={() => verComprobante(j)} data-testid="ver-comprobante">
                    <Paperclip size={13} className="mr-1 inline" /> Ver comprobante
                  </button>
                )}
                {j.estado === "pendiente" && !esLiq && (
                  <>
                    <button className={`${btnPrim} !px-3 !py-1.5 text-xs`} onClick={() => abrirResolver(j)} data-testid="btn-aprobar">
                      <Check size={13} className="mr-1 inline" /> Aprobar
                    </button>
                    <button
                      className="rounded-xl border border-red-400/40 bg-red-400/10 px-3 py-1.5 text-xs font-semibold text-red-300 transition-colors hover:bg-red-400/20"
                      onClick={() => abrirResolver(j)}
                      data-testid="btn-rechazar"
                    >
                      <X size={13} className="mr-1 inline" /> Rechazar
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
        <footer className="flex items-center justify-between border-t border-white/8 px-5 py-3.5 text-xs text-gray-500">
          <span data-testid="just-paginacion">
            Página {pagina} de {paginas} · {filtrados.length} justificativos
          </span>
          <span className="flex gap-2">
            <button className={`${btnSec} !px-2.5 !py-1 text-xs`} disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} data-testid="just-anterior">
              <ChevronLeft size={13} className="mr-0.5 inline" /> Anterior
            </button>
            <button className={`${btnSec} !px-2.5 !py-1 text-xs`} disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)} data-testid="just-siguiente">
              Siguiente <ChevronRight size={13} className="ml-0.5 inline" />
            </button>
          </span>
        </footer>
      </Card>

      {resolver && (
        <Modal titulo={`Revisar justificativo — ${resolver.empleado_nombre}`} onClose={() => setResolver(null)} testid="modal-resolver">
          <p className="mb-1 text-sm text-gray-300">
            <span className="font-semibold text-white">{TIPO_JUST[resolver.tipo]}</span> del {fmtFecha(resolver.fecha)}
          </p>
          <p className="mb-4 text-sm text-gray-400">{resolver.descripcion}</p>
          {!resolver.archivo_url && (
            <p className="mb-4 rounded-xl border border-red-400/40 bg-red-400/10 px-3 py-2 text-xs text-red-300">
              Sin comprobante no se puede aprobar; el día queda como falta injustificada.
            </p>
          )}
          {resolver.archivo_url && (
            <button className={`${btnSec} mb-4 text-xs`} onClick={() => verComprobante(resolver)} data-testid="resolver-ver-comprobante">
              <Paperclip size={13} className="mr-1 inline" /> Ver comprobante
            </button>
          )}
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Comentario</label>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={2}
            placeholder="Opcional — queda registrado"
            className={`${inputCls} w-full resize-none`}
            data-testid="resolver-comentario"
          />
          {(resolver.tipo === "tardanza" || resolver.tipo === "retiro_anticipado") && (
            <label className="mt-3 flex items-center gap-2.5 text-sm text-gray-300" data-testid="resolver-descuenta-label">
              <input
                type="checkbox"
                checked={descuenta}
                onChange={(e) => setDescuenta(e.target.checked)}
                className="h-4 w-4 accent-[#2DD4BF]"
                data-testid="resolver-descuenta"
              />
              Descuenta la adicional del día
            </label>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button
              className="rounded-xl border border-red-400/40 bg-red-400/10 px-4 py-2 text-sm font-semibold text-red-300 transition-colors hover:bg-red-400/20 disabled:opacity-40"
              disabled={mutResolver.isPending}
              onClick={() => mutResolver.mutate({ id: resolver.id, accion: "rechazado" })}
              data-testid="confirmar-rechazar"
            >
              Rechazar
            </button>
            <button
              className={btnPrim}
              disabled={mutResolver.isPending || !resolver.archivo_url}
              onClick={() => mutResolver.mutate({ id: resolver.id, accion: "aprobado" })}
              data-testid="confirmar-aprobar"
            >
              Aprobar
            </button>
          </div>
        </Modal>
      )}

      {cargar && (
        <Modal titulo="Cargar justificativo a nombre de un empleado" onClose={() => setCargar(null)} testid="modal-cargar-just">
          <div className="space-y-3.5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Empleado</label>
              <select value={cargar.empleado_id} onChange={(e) => setCargar({ ...cargar, empleado_id: e.target.value })} className={`${inputCls} w-full`} data-testid="cargar-empleado">
                <option value="">Elegí el empleado…</option>
                {empleados?.filter((e) => e.activo).map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre} ({e.legajo})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className={`${inputCls} w-full`} data-testid="cargar-tipo">
                  {Object.entries(TIPO_JUST).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Fecha</label>
                <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={`${inputCls} w-full`} data-testid="cargar-fecha" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Descripción</label>
              <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="ej. llegó por WhatsApp" className={`${inputCls} w-full`} data-testid="cargar-descripcion" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Comprobante (obligatorio)</label>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => setArchivo(e.target.files?.[0] || null)} className="hidden" />
              <button className={`${btnSec} w-full`} onClick={() => fileRef.current?.click()} data-testid="cargar-archivo">
                <Paperclip size={14} className="mr-1.5 inline" /> {archivo ? archivo.name : "Adjuntar archivo (foto o PDF)"}
              </button>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button className={btnSec} onClick={() => setCargar(null)}>Cancelar</button>
            <button
              className={btnPrim}
              disabled={mutCargar.isPending || !cargar.empleado_id || !form.fecha || !archivo}
              onClick={() => mutCargar.mutate()}
              data-testid="cargar-confirmar"
            >
              Cargar
            </button>
          </div>
        </Modal>
      )}

      {visor && <VisorArchivo url={visor.url} onClose={() => setVisor(null)} />}
    </motion.div>
  );
}
